import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, CheckCircle, Mail, Phone, User, MessageSquare, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function TableBookingWidget({ restaurantId, onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Date/Guests/Time selection, 2: Customer Details, 3: Success Confirmation
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  // Settings returned from availability endpoint
  const [minGroup, setMinGroup] = useState(1);
  const [maxGroup, setMaxGroup] = useState(10);
  
  // Form fields
  const [numGuests, setNumGuests] = useState(2);
  const [bookingDate, setBookingDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  
  const [reservationCode, setReservationCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Auto-fill user details if logged in
  useEffect(() => {
    if (user) {
      if (user.full_name) setCustomerName(user.full_name);
      else if (user.name) setCustomerName(user.name);
      
      if (user.email) setCustomerEmail(user.email);
      
      if (user.phone) setCustomerPhone(user.phone);
      else if (user.phone_number) setCustomerPhone(user.phone_number);
    }
  }, [user]);

  // Fetch slots whenever date changes
  useEffect(() => {
    if (restaurantId && bookingDate) {
      fetchAvailableSlots();
    }
  }, [bookingDate, restaurantId]);

  const fetchAvailableSlots = async () => {
    try {
      setSlotsLoading(true);
      setErrorMsg('');
      const res = await api.get(`/api/reservations/availability?date=${bookingDate}`);
      
      if (res.data.enabled) {
        setAvailableSlots(res.data.slots || []);
        if (res.data.settings) {
          setMinGroup(res.data.settings.min_group_size || 1);
          setMaxGroup(res.data.settings.max_group_size || 10);
        }
        // Auto-clear slot if previously selected slot is not in the new list or unavailable
        const stillAvailable = res.data.slots.find(s => s.time === selectedSlot && s.available);
        if (!stillAvailable) setSelectedSlot('');
      } else {
        setAvailableSlots([]);
        setErrorMsg('Table reservations are currently closed for this location.');
      }
      setSlotsLoading(false);
    } catch (err) {
      console.error('Failed to check table availability:', err);
      setErrorMsg('Failed to check slot availability. Please try again.');
      setSlotsLoading(false);
    }
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setErrorMsg('Please select a booking time slot.');
      return;
    }
    setErrorMsg('');
    setStep(2);
  };

  const handleConfirmReservation = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErrorMsg('');
      
      const payload = {
        restaurantId,
        customerName,
        customerEmail,
        customerPhone,
        numGuests,
        reservationDate: bookingDate,
        reservationTime: selectedSlot,
        specialRequests
      };

      const res = await api.post('/api/reservations', payload);
      if (res.data.success) {
        setReservationCode(res.data.reservation.reservation_code);
        setStep(3);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to create reservation:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to complete booking. Please try again.');
      setLoading(false);
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const maxDateStr = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  })();

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full border border-slate-100 shadow-2xl relative font-sans">
      
      {/* Title */}
      {step < 3 && (
        <div className="mb-6">
          <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2">
            <Calendar className="text-primary-600" />
            Reserve a Table
          </h2>
          <p className="text-xs text-slate-400 mt-1">Book your table instantly. No booking fees.</p>
        </div>
      )}

      {/* Error indicator */}
      {errorMsg && (
        <div className="mb-5 bg-red-50 text-red-700 border border-red-100 p-4 rounded-2xl flex items-start gap-3 text-sm animate-in fade-in slide-in-from-top-2">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* STEP 1: Slots select */}
      {step === 1 && (
        <form onSubmit={handleNextStep} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                <Calendar size={12} /> Select Date
              </label>
              <input 
                type="date"
                min={todayStr}
                max={maxDateStr}
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                <Users size={12} /> Guests
              </label>
              <select
                value={numGuests}
                onChange={(e) => setNumGuests(Number(e.target.value))}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
              >
                {Array.from({ length: maxGroup - minGroup + 1 }, (_, i) => minGroup + i).map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
              <Clock size={12} /> Select Time Slot
            </label>
            
            {slotsLoading ? (
              <div className="py-8 text-center text-slate-400">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <span className="text-xs font-semibold">Checking available tables...</span>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="p-5 text-center bg-slate-50 rounded-2xl text-slate-400 text-xs border border-dashed border-slate-200">
                No slots available for the selected date.
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto pr-1">
                {availableSlots.map((s) => (
                  <button
                    key={s.time}
                    type="button"
                    disabled={!s.available}
                    onClick={() => setSelectedSlot(s.time)}
                    className={`py-2 px-3 text-xs font-bold rounded-xl border transition-all ${
                      !s.available 
                        ? 'bg-slate-100/50 text-slate-300 border-slate-100 cursor-not-allowed' 
                        : selectedSlot === s.time
                          ? 'bg-slate-900 border-slate-900 text-amber-300 shadow-md scale-105'
                          : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700'
                    }`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-50 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 active:scale-95 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedSlot || slotsLoading}
              className="w-1/2 py-3 bg-slate-950 text-white hover:bg-primary-600 rounded-xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all disabled:opacity-50 disabled:pointer-events-none hover:shadow-lg"
            >
              Next Step
            </button>
          </div>
        </form>
      )}

      {/* STEP 2: Customer Details */}
      {step === 2 && (
        <form onSubmit={handleConfirmReservation} className="space-y-4">
          
          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-center justify-between text-xs font-bold text-slate-600 mb-2">
            <div className="flex items-center gap-1.5"><Calendar size={14} /> {bookingDate}</div>
            <div className="flex items-center gap-1.5"><Clock size={14} /> {selectedSlot}</div>
            <div className="flex items-center gap-1.5"><Users size={14} /> {numGuests} Guests</div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
              <User size={12} /> Contact Name
            </label>
            <input 
              type="text" 
              required
              placeholder="Your Full Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-medium outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                <Mail size={12} /> Email Address
              </label>
              <input 
                type="email" 
                required
                placeholder="email@example.com"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-medium outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
                <Phone size={12} /> Phone Number
              </label>
              <input 
                type="tel" 
                required
                placeholder="+91 99999 00000"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-medium outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1">
              <MessageSquare size={12} /> Special Requests (Optional)
            </label>
            <textarea 
              rows="2"
              placeholder="e.g. Window table, kid high chair, food allergies..."
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm font-medium outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="pt-4 border-t border-slate-50 flex gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-1/2 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 active:scale-95 transition-all"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-1/2 py-3 bg-slate-950 text-white hover:bg-primary-600 rounded-xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all disabled:opacity-50 disabled:pointer-events-none hover:shadow-lg flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Booking...</span>
                </>
              ) : (
                'Book Table'
              )}
            </button>
          </div>
        </form>
      )}

      {/* STEP 3: Success Confirmation Card */}
      {step === 3 && (
        <div className="text-center py-6 space-y-6 animate-in zoom-in-95 duration-200">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-500 border border-emerald-100 rounded-full flex items-center justify-center mx-auto shadow-md">
            <CheckCircle size={36} />
          </div>

          <div>
            <h2 className="text-2xl font-black text-slate-900">Reservation Placed!</h2>
            <p className="text-xs text-slate-400 mt-1.5">We have received your table reservation request.</p>
          </div>

          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4 max-w-sm mx-auto">
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Reservation Code</span>
              <h3 className="text-2xl font-black text-slate-900 mt-1 tracking-wider">{reservationCode}</h3>
            </div>
            
            <div className="border-t border-slate-200/60 pt-3 grid grid-cols-3 gap-2 text-xs font-bold text-slate-600">
              <div>
                <span className="block text-[9px] font-black uppercase text-slate-400">Date</span>
                <span className="block mt-0.5">{bookingDate}</span>
              </div>
              <div>
                <span className="block text-[9px] font-black uppercase text-slate-400">Time</span>
                <span className="block mt-0.5">{selectedSlot}</span>
              </div>
              <div>
                <span className="block text-[9px] font-black uppercase text-slate-400">Guests</span>
                <span className="block mt-0.5">{numGuests} Pax</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
            A confirmation has been sent to <span className="font-semibold text-slate-700">{customerEmail}</span>. Please present this code to the restaurant host when you arrive.
          </p>

          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 bg-slate-950 hover:bg-primary-600 text-white rounded-xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all shadow-md hover:shadow-lg"
          >
            Done
          </button>
        </div>
      )}

    </div>
  );
}
